import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Circle, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

/**
 * Sidebar onboarding checklist — only visible for shop_admin users whose
 * `shops.onboarding_completed_at IS NULL`. Shows the same 4 milestones as
 * the `/welcome` wizard so the owner can see at a glance what's left.
 *
 * Milestones:
 *   1. Shop profile   — name + subdomain filled (welcome text is nice-to-have)
 *   2. Add services   — at least 1 row in services
 *   3. Connect payments — shops.stripe_account_id present
 *   4. Add a barber   — at least 1 row in barbers
 */
export const OnboardingChecklist = ({ profile }) => {
  const shop = profile?.shops || null;
  const isShopAdmin = profile?.role === 'shop_admin';
  const incomplete = shop && !shop.onboarding_completed_at;

  const [servicesCount, setServicesCount] = useState(0);
  const [barbersCount, setBarbersCount]   = useState(0);
  const [loaded, setLoaded]               = useState(false);

  useEffect(() => {
    if (!isShopAdmin || !incomplete || !shop?.id) return;
    (async () => {
      const [svc, brb] = await Promise.all([
        supabase.from('services').select('id', { count: 'exact', head: true }).eq('shop_id', shop.id),
        supabase.from('barbers').select('id',  { count: 'exact', head: true }).eq('shop_id', shop.id),
      ]);
      setServicesCount(svc.count || 0);
      setBarbersCount(brb.count || 0);
      setLoaded(true);
    })();
  }, [isShopAdmin, incomplete, shop?.id]);

  if (!isShopAdmin || !incomplete) return null;

  const items = [
    { key: 'profile',  label: 'Shop profile',     done: !!(shop.name && shop.subdomain) },
    { key: 'services', label: 'Add services',     done: servicesCount > 0 },
    { key: 'payments', label: 'Connect payments', done: !!shop.stripe_account_id },
    { key: 'barber',   label: 'Add a barber',     done: barbersCount > 0 },
  ];
  const doneCount = items.filter(i => i.done).length;

  return (
    <div
      data-testid="sidebar-onboarding-checklist"
      style={{
        margin: '14px 4px 0',
        padding: '12px 12px 10px',
        border: '1px solid var(--line)',
        borderRadius: 12,
        background: 'var(--paper)',
        fontSize: 12.5,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
        <b style={{ fontSize: 12.5, letterSpacing: '-0.005em' }}>Finish setup</b>
        <span data-testid="checklist-progress" style={{ fontSize: 11, color: 'var(--mute)' }}>{doneCount}/{items.length}</span>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '2px 0 8px' }}>
        {items.map(item => (
          <li
            key={item.key}
            data-testid={`checklist-${item.key}`}
            data-done={item.done ? 'true' : 'false'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 0',
              color: item.done ? 'var(--mute)' : 'var(--ink)',
              textDecoration: item.done ? 'line-through' : 'none',
            }}
          >
            {item.done
              ? <Check size={13} stroke="#10b981" strokeWidth={3} />
              : <Circle size={13} stroke="currentColor" strokeWidth={2} />}
            <span>{item.label}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/welcome"
        data-testid="checklist-continue-link"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--acc)',
          textDecoration: 'none',
        }}
      >
        {doneCount === items.length ? 'Wrap up' : 'Continue'} <ArrowRight size={12} />
      </Link>
      {!loaded && (
        <div style={{ fontSize: 10.5, color: 'var(--mute2)', marginTop: 4 }} data-testid="checklist-loading">
          Checking…
        </div>
      )}
    </div>
  );
};
